import axios from 'axios'

/**
 * nn's error envelope is flat — `{ error: "CODE" }` — and carries no code field,
 * so the message string *is* the machine channel. `parsePaymentsValidationError`
 * does not apply: it only reads nectar's nested `{ error: { code, details } }`.
 */

/** Every documented Buzz link failure, pinned to the status it arrives at. */
const BUZZ_LINK_ERROR_STATUS: Record<string, number> = {
  BUZZ_CODE_INVALID: 400,
  BUZZ_CODE_MALFORMED: 400,
  BUZZ_ALREADY_LINKED: 409,
  BUZZ_ACCOUNT_TAKEN: 409,
  BUZZ_LINK_RATE_LIMITED: 429,
  BUZZ_UNAVAILABLE: 503
}

export type BuzzLinkErrorCode = keyof typeof BUZZ_LINK_ERROR_STATUS | 'BUZZ_UNKNOWN'

export type BuzzLinkError = {
  code: BuzzLinkErrorCode
  /** Integer seconds off `Retry-After`; `null` means the form states no number. */
  retryAfterSeconds: number | null
}

/**
 * Only the integer-seconds form of `Retry-After` is read. The HTTP-date form —
 * which nn does not send — yields `null` rather than a guessed delta, and `null`
 * renders as the number-free copy.
 */
function readRetryAfterSeconds(headers: unknown): number | null {
  if (typeof headers !== 'object' || headers === null) {
    return null
  }

  const raw = (headers as Record<string, unknown>)['retry-after']
  if (typeof raw !== 'string' && typeof raw !== 'number') {
    return null
  }

  const text = String(raw).trim()
  if (!/^\d+$/.test(text)) {
    return null
  }

  const seconds = Number(text)
  return Number.isSafeInteger(seconds) ? seconds : null
}

/**
 * Maps a rejected link/unlink call onto a code the form can branch on. Anything
 * unrecognised — a non-axios throw, no response, a non-object body, an unlisted
 * string, or a known string at an undocumented status — is `BUZZ_UNKNOWN`.
 */
export function parseBuzzLinkError(err: unknown): BuzzLinkError {
  if (!axios.isAxiosError(err) || err.response === undefined) {
    return { code: 'BUZZ_UNKNOWN', retryAfterSeconds: null }
  }

  const { status, data, headers } = err.response
  const retryAfterSeconds = readRetryAfterSeconds(headers)

  if (typeof data !== 'object' || data === null) {
    return { code: 'BUZZ_UNKNOWN', retryAfterSeconds }
  }

  const code = (data as { error?: unknown }).error
  if (typeof code !== 'string' || BUZZ_LINK_ERROR_STATUS[code] !== status) {
    return { code: 'BUZZ_UNKNOWN', retryAfterSeconds }
  }

  return { code: code as BuzzLinkErrorCode, retryAfterSeconds }
}
